import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import DatePickerWithInput from './LargeDatePicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RESULT_OPTIONS = [
  { value: 'accepted', label: 'Δεκτή' },
  { value: 'partially_accepted', label: 'Εν μέρει δεκτή' },
  { value: 'rejected', label: 'Απορρίπτεται' },
];

// Configuration per option type — drives which inputs to render and what to create
// Keyed by `${procedureType}:${optionValue}` for procedure-specific flows or just optionValue for shared.
const ACTION_CONFIG = {
  // ===== ADMINISTRATIVE =====
  'administrative:first_instance_service': {
    label: 'Επίδοση οριστικής απόφασης Διοικ. Πρωτοδικείου',
    template_id: 'dioikitiki_epidosi_protodikeiou_loipes',
    deadline_id: 'efesi_dioik_protod',
    deadline_title: 'Προθεσμία έφεσης',
    base_field: 'service_date',
    base_label: 'Ημερομηνία επίδοσης *',
    show_result: true,
    main_title: 'Επίδοση οριστικής απόφασης Δ. Πρωτοδικείου',
  },
  'administrative:first_instance_publication': {
    label: 'Δημοσίευση οριστικής απόφασης Διοικ. Πρωτοδικείου (χωρίς επίδοση)',
    template_id: 'dioikitiki_dimosieysi_protodikeiou',
    deadline_id: 'kataxristiki_efesi_dioik_protod',
    deadline_title: 'Καταχρηστική προθεσμία έφεσης',
    base_field: 'publication_date',
    base_label: 'Ημερομηνία δημοσίευσης *',
    show_decision_number: true,
    show_result: true,
    main_title: 'Δημοσίευση οριστικής απόφασης Δ. Πρωτοδικείου',
  },
  'administrative:appeal_service': {
    label: 'Επίδοση απόφασης Διοικ. Εφετείου',
    template_id: 'dioikitiki_epidosi_efeteiou',
    deadline_id: 'anairesi_dioik_efet',
    deadline_title: 'Προθεσμία αναίρεσης',
    base_field: 'service_date',
    base_label: 'Ημερομηνία επίδοσης *',
    show_result: true,
    main_title: 'Επίδοση απόφασης Δ. Εφετείου',
  },
  'administrative:appeal_publication': {
    label: 'Δημοσίευση οριστικής απόφασης Διοικ. Εφετείου (χωρίς επίδοση)',
    template_id: 'dioikitiki_dimosieysi_efeteiou',
    deadline_id: 'kataxristiki_anairesi_dioik_efet',
    deadline_title: 'Καταχρηστική προθεσμία αναίρεσης',
    base_field: 'publication_date',
    base_label: 'Ημερομηνία δημοσίευσης *',
    show_decision_number: true,
    show_result: true,
    main_title: 'Δημοσίευση οριστικής απόφασης Δ. Εφετείου',
  },
  'administrative:appeal_cancellation_service': {
    label: 'Επίδοση απόφασης Δ. Εφετείου επί αιτήσεως ακύρωσης',
    template_id: 'dioikitiki_epidosi_efeteiou_akyrosis',
    deadline_id: 'efesi_dioik_efet_akyrosis',
    deadline_title: 'Προθεσμία έφεσης (58 παρ. 3 ΣτΕ)',
    base_field: 'service_date',
    base_label: 'Ημερομηνία επίδοσης *',
    show_result: true,
    main_title: 'Επίδοση απόφασης Δ. Εφετείου επί αιτήσεως ακύρωσης',
  },
  'administrative:appeal_cancellation_publication': {
    label: 'Δημοσίευση απόφασης Δ. Εφετείου επί αιτήσεως ακύρωσης (χωρίς επίδοση)',
    template_id: 'dioikitiki_dimosieysi_efeteiou_akyrosis',
    deadline_id: 'kataxristiki_efesi_dioik_efet_akyrosis',
    deadline_title: 'Προθεσμία έφεσης (58 παρ. 3 ΣτΕ)',
    base_field: 'publication_date',
    base_label: 'Ημερομηνία δημοσίευσης *',
    show_decision_number: true,
    show_result: true,
    main_title: 'Δημοσίευση απόφασης Δ. Εφετείου επί αιτήσεως ακύρωσης',
  },
  'administrative:final_rejection': {
    label: 'Τελεσίδικη απόρριψη αγωγής/προσφυγής για τυπικούς λόγους',
    template_id: 'dioikitiki_telesidiki_aporripsi',
    deadline_id: 'ek_neou_dioik',
    deadline_title: 'Εκ νέου άσκηση ενδίκου βοηθήματος',
    base_field: 'service_date',
    base_label: 'Ημερομηνία επίδοσης ή δημοσίευσης τελεσίδικης απόφασης *',
    main_title: 'Τελεσίδικη απόρριψη για τυπικούς λόγους',
  },
  'administrative:ste_filing': {
    label: 'Κατάθεση δικογράφου ΣτΕ (επίδοση εντός 2 μηνών)',
    template_id: 'dioikitiki_ste_katathesi',
    deadline_id: 'epidosi_eisagogikou_ste',
    deadline_title: 'Επίδοση εισαγωγικού δικογράφου ΣτΕ',
    base_field: 'event_date',
    base_label: 'Ημερομηνία κατάθεσης *',
    main_title: 'Κατάθεση δικογράφου ΣτΕ',
  },
  'administrative:ste_appeal_service': {
    label: 'Επίδοση ενδίκου μέσου ΣτΕ (πρόσθετοι λόγοι)',
    template_id: 'dioikitiki_ste_epidosi',
    deadline_id: 'prosthetoi_logoi_ste_trimino',
    deadline_title: 'Κατάθεση πρόσθετων λόγων ΣτΕ',
    base_field: 'event_date',
    base_label: 'Ημερομηνία επίδοσης ενδίκου μέσου / αποστολής φακέλου *',
    main_title: 'Επίδοση ενδίκου μέσου ΣτΕ',
  },

  // ===== CIVIL — απλά flows (single-deadline, no branching) =====
  'civil:cancellation': {
    label: 'Ματαίωση συζήτησης',
    template_id: 'astiki_mataiosi',
    deadline_id: 'epanafora_klisi',
    deadline_title: 'Προθεσμία επαναφοράς με κλήση',
    base_field: 'event_date',
    base_label: 'Ημερομηνία ματαίωσης *',
    main_title: 'Ματαίωση συζήτησης',
  },
  'civil:withdrawal': {
    label: 'Παραίτηση από δικόγραφο',
    template_id: 'astiki_paraitisi',
    deadline_id: 'ek_neou_agogi',
    deadline_title: 'Προθεσμία εκ νέου άσκησης αγωγής',
    base_field: 'event_date',
    base_label: 'Ημερομηνία παραίτησης *',
    main_title: 'Παραίτηση από δικόγραφο',
  },
  'civil:final_decision_publication': {
    label: 'Δημοσίευση οριστικής απόφασης (χωρίς επίδοση)',
    template_id: 'astiki_dimosieysi_oristikis',
    deadline_id: 'kataxristiki_efesis',
    deadline_title: 'Καταχρηστική προθεσμία έφεσης/αναίρεσης',
    base_field: 'publication_date',
    base_label: 'Ημερομηνία δημοσίευσης *',
    show_decision_number: true,
    show_result: true,
    main_title: 'Δημοσίευση οριστικής απόφασης (χωρίς επίδοση)',
  },
  'civil:final_rejection': {
    label: 'Τελεσίδικη απόρριψη αγωγής για τυπικούς λόγους',
    template_id: 'astiki_telesidiki_aporripsi',
    deadline_id: 'ek_neou_agogi_aporripsi',
    deadline_title: 'Προθεσμία εκ νέου άσκησης αγωγής',
    base_field: 'publication_date',
    base_label: 'Ημερομηνία δημοσίευσης τελεσίδικης απόφασης *',
    main_title: 'Τελεσίδικη απόρριψη αγωγής (τυπικοί λόγοι)',
  },
  'civil:payment_order_issue': {
    label: 'Έκδοση διαταγής πληρωμής',
    template_id: 'astiki_ekdosi_dp',
    deadline_id: 'epidosi_dp',
    deadline_title: 'Προθεσμία επίδοσης διαταγής πληρωμής',
    base_field: 'event_date',
    base_label: 'Ημερομηνία έκδοσης *',
    main_title: 'Έκδοση διαταγής πληρωμής',
  },
  'civil:katataxi_prosklisi': {
    label: 'Επίδοση πρόσκλησης για πίνακα κατάταξης',
    template_id: 'astiki_pinakas_katataxis',
    deadline_id: 'anakopi_979',
    deadline_title: 'Προθεσμία ανακοπής κατά πίνακα κατάταξης',
    base_field: 'service_date',
    base_label: 'Ημερομηνία επίδοσης πρόσκλησης *',
    main_title: 'Επίδοση πρόσκλησης για πίνακα κατάταξης',
  },
  'civil:diataxi_prodikastiki': {
    label: 'Έκδοση Διάταξης προδικαστικού ελέγχου (237 ΚΠολΔ)',
    template_id: 'astiki_ekdosi_diataxis',
    deadline_id: 'antilexi_diataxis',
    deadline_title: 'Προθεσμία αντίλεξης κατά της Διάταξης',
    base_field: 'event_date',
    base_label: 'Ημερομηνία έκδοσης Διάταξης *',
    main_title: 'Έκδοση Διάταξης προδικαστικού ελέγχου',
  },
};

const OtherActionForm = ({ entry, optionValue, procedureType = 'civil', onCreated, onCancel }) => {
  const [data, setData] = useState({
    service_date: '',
    publication_date: '',
    event_date: '',
    decision_number: '',
    result: '',
    hearing_date: '',
    sworn_count: 1,
    sworn_dates: [''],
    is_public: 'no',           // Αντίδικος το Ελληνικό Δημόσιο (πολιτικές)
    // Branching: Επίδοση οριστικής (civil)
    fds_court_level: '',      // 'first' | 'appeal'
    fds_microdiff: '',         // 'yes' | 'no'
    fds_erimin: '',            // 'yes' | 'no'
    fds_telesidiki: '',        // 'yes' | 'no'
    // Branching: Επίδοση διαταγής
    pos_kind: '',              // 'payment' | 'lease'
    pos_service_no: '',        // 'first' | 'second'
    // Branching: Πράξη αναγκαστικής εκτέλεσης
    enf_remedy: '',            // '933' | '954'
    enf_954_old: '',           // 'yes' | 'no' — επίδοση επιταγής πριν την 1.1.2026;
    enf_934_kind: '',          // '934a' | '934b'
    enf_934a: '',              // 'seizure' | 'immediate'
    enf_934b: '',              // 'last_action' | 'auction_movables' | 'auction_immovables'
  });
  const [submitting, setSubmitting] = useState(false);

  // Reset when option changes
  useEffect(() => {
    setData({
      service_date: '', publication_date: '', event_date: '',
      decision_number: '', result: '', hearing_date: '',
      sworn_count: 1, sworn_dates: [''],
      is_public: 'no',
      fds_court_level: '', fds_microdiff: '', fds_erimin: '', fds_telesidiki: '',
      pos_kind: '', pos_service_no: '',
      enf_remedy: '', enf_934_kind: '', enf_934a: '', enf_934b: '', enf_954_old: '',
    });
  }, [optionValue]);

  // Config lookup by procedure-scoped key
  const configKey = `${procedureType}:${optionValue}`;
  const config = ACTION_CONFIG[configKey];

  // Helper: create entry via API (no credit-charge wrapper here — backend already handles)
  const createEntry = async (entryData) => {
    return axios.post(`${API}/entries`, entryData);
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()}`;
  };

  const sharedEntryFields = () => ({
    client_id: entry.client_id,
    case_id: entry.case_id || '',
    opponent_details: entry.opponent_details || null,
    case_description_type: entry.case_description_type || null,
    case_description_custom: entry.case_description_custom || null,
  });

  const submitStandard = async () => {
    if (!config) return;
    const baseDate = data[config.base_field];
    if (!baseDate) {
      toast.error('Συμπληρώστε την ημερομηνία');
      return;
    }
    setSubmitting(true);
    try {
      // 1) Calculate deadline from template
      const calcRes = await axios.post(`${API}/calculate-from-template`, {
        template_id: config.template_id,
        base_date: baseDate,
        selected_deadline_ids: [config.deadline_id],
        options: procedureType === 'civil' ? { is_public_entity: data.is_public === 'yes' } : {},
      });
      const deadlines = calcRes.data?.deadlines || [];
      if (deadlines.length === 0) {
        toast.error('Δεν επιστράφηκαν προθεσμίες από το server');
        setSubmitting(false);
        return;
      }

      // 2) Create the "main" event entry as completed
      const resultLabel = data.result
        ? RESULT_OPTIONS.find((o) => o.value === data.result)?.label
        : null;
      const decisionLabel = data.decision_number
        ? `Αρ. απόφασης: ${data.decision_number}`
        : null;
      const baseFieldLabel =
        config.base_field === 'service_date'
          ? `Ημ/νία επίδοσης: ${fmtDate(baseDate)}`
          : config.base_field === 'publication_date'
          ? `Ημ/νία δημοσίευσης: ${fmtDate(baseDate)}`
          : `Ημ/νία: ${fmtDate(baseDate)}`;
      const noteParts = [decisionLabel, baseFieldLabel, resultLabel ? `Αποτέλεσμα: ${resultLabel}` : null].filter(Boolean);

      await createEntry({
        ...sharedEntryFields(),
        entry_type: 'court_action',
        title: config.main_title,
        date: baseDate,
        priority: 'medium',
        status: 'ΟΛΟΚΛΗΡΩΜΕΝΗ',
        notes: noteParts.join(' — '),
      });

      // 3) Create the deadline entry/entries
      for (const d of deadlines) {
        await createEntry({
          ...sharedEntryFields(),
          entry_type: 'court_action',
          title: config.deadline_title,
          date: d.deadline_date,
          priority: 'high',
          status: 'ΣΕ ΑΝΑΜΟΝΗ',
          notes: `${d.article || ''} — ${baseFieldLabel}`.trim(),
        });
      }

      toast.success('Καταχωρήθηκε επιτυχώς');
      onCreated && onCreated();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.detail || err.message || 'Σφάλμα κατά την καταχώρηση';
      toast.error(typeof msg === 'string' ? msg : 'Σφάλμα');
    } finally {
      setSubmitting(false);
    }
  };

  // Helper: invoke template + create main + deadlines (used by branching flows)
  const calculateAndCreate = async ({ template_id, deadline_id, deadline_title, main_title, base_date, base_label, result_label, options = {} }) => {
    const calcRes = await axios.post(`${API}/calculate-from-template`, {
      template_id, base_date, selected_deadline_ids: [deadline_id],
      options: { is_public_entity: data.is_public === 'yes', ...options },
    });
    const deadlines = calcRes.data?.deadlines || [];
    const noteParts = [base_label, result_label ? `Αποτέλεσμα: ${result_label}` : null].filter(Boolean);
    await createEntry({
      ...sharedEntryFields(), entry_type: 'court_action', title: main_title,
      date: base_date, priority: 'medium', status: 'ΟΛΟΚΛΗΡΩΜΕΝΗ', notes: noteParts.join(' — '),
    });
    for (const d of deadlines) {
      await createEntry({
        ...sharedEntryFields(), entry_type: 'court_action', title: deadline_title,
        date: d.deadline_date, priority: 'high', status: 'ΣΕ ΑΝΑΜΟΝΗ',
        notes: `${d.article || ''} — ${base_label}`.trim(),
      });
    }
  };

  // ===== Branching submit: civil Επίδοση οριστικής απόφασης =====
  const submitFinalDecisionService = async () => {
    if (!data.fds_court_level) { toast.error('Επιλέξτε βαθμό δικαιοδοσίας'); return; }
    if (!data.service_date) { toast.error('Συμπληρώστε ημερομηνία επίδοσης'); return; }

    const resultLabel = data.result
      ? RESULT_OPTIONS.find((o) => o.value === data.result)?.label
      : null;
    const baseLabel = `Ημ/νία επίδοσης: ${fmtDate(data.service_date)}`;

    setSubmitting(true);
    try {
      if (data.fds_court_level === 'first') {
        if (!data.fds_microdiff) { toast.error('Διευκρινίστε αν αφορά μικροδιαφορά'); setSubmitting(false); return; }
        if (data.fds_microdiff === 'yes') {
          // Δεν προβλέπεται έφεση — μόνο main entry
          await createEntry({
            ...sharedEntryFields(), entry_type: 'court_action',
            title: 'Επίδοση οριστικής απόφασης (μικροδιαφορά)',
            date: data.service_date, priority: 'medium', status: 'ΟΛΟΚΛΗΡΩΜΕΝΗ',
            notes: [baseLabel, 'Δεν προβλέπεται η άσκηση έφεσης (μικροδιαφορά)', resultLabel ? `Αποτέλεσμα: ${resultLabel}` : null].filter(Boolean).join(' — '),
          });
        } else {
          if (!data.fds_erimin) { toast.error('Διευκρινίστε αν εκδόθηκε ερήμην'); setSubmitting(false); return; }
          if (data.fds_erimin === 'yes') {
            await calculateAndCreate({
              template_id: 'astiki_epidosi_oristikis_erimin',
              deadline_id: 'efesi_erimin',
              deadline_title: 'Προθεσμία έφεσης (ερήμην)',
              main_title: 'Επίδοση οριστικής απόφασης (ερήμην, πρωτοβάθμιο)',
              base_date: data.service_date, base_label: baseLabel, result_label: resultLabel,
            });
            await calculateAndCreate({
              template_id: 'astiki_epidosi_oristikis_erimin',
              deadline_id: 'anakopi_erimodik_protov',
              deadline_title: 'Προθεσμία ανακοπής ερημοδικίας',
              main_title: 'Επίδοση οριστικής απόφασης (ερήμην) — ανακοπή ερημοδικίας',
              base_date: data.service_date, base_label: baseLabel, result_label: resultLabel,
            });
          } else {
            await calculateAndCreate({
              template_id: 'astiki_epidosi_oristikis',
              deadline_id: 'efesi_30',
              deadline_title: 'Προθεσμία έφεσης',
              main_title: 'Επίδοση οριστικής απόφασης',
              base_date: data.service_date, base_label: baseLabel, result_label: resultLabel,
            });
          }
        }
      } else {
        // Δευτεροβάθμιο — τελεσίδικη
        if (!data.fds_telesidiki) { toast.error('Διευκρινίστε αν είναι τελεσίδικη'); setSubmitting(false); return; }
        if (data.fds_telesidiki === 'yes') {
          await calculateAndCreate({
            template_id: 'astiki_epidosi_telesidikis',
            deadline_id: 'anairesi_60',
            deadline_title: 'Προθεσμία αίτησης αναίρεσης',
            main_title: 'Επίδοση τελεσίδικης απόφασης',
            base_date: data.service_date, base_label: baseLabel, result_label: resultLabel,
          });
        } else {
          await createEntry({
            ...sharedEntryFields(), entry_type: 'court_action',
            title: 'Επίδοση οριστικής απόφασης (δευτεροβάθμιο)',
            date: data.service_date, priority: 'medium', status: 'ΟΛΟΚΛΗΡΩΜΕΝΗ',
            notes: [baseLabel, resultLabel ? `Αποτέλεσμα: ${resultLabel}` : null].filter(Boolean).join(' — '),
          });
        }
      }
      toast.success('Καταχωρήθηκε επιτυχώς');
      onCreated && onCreated();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || err.message || 'Σφάλμα');
    } finally {
      setSubmitting(false);
    }
  };

  // ===== Branching submit: Επίδοση διαταγής πληρωμής/απόδοσης μισθίου =====
  const submitPaymentOrderService = async () => {
    if (!data.pos_kind) { toast.error('Επιλέξτε τύπο διαταγής'); return; }
    if (!data.service_date) { toast.error('Συμπληρώστε ημερομηνία επίδοσης'); return; }
    setSubmitting(true);
    try {
      const baseLabel = `Ημ/νία επίδοσης: ${fmtDate(data.service_date)}`;
      if (data.pos_kind === 'lease') {
        await calculateAndCreate({
          template_id: 'astiki_epidosi_dapm', deadline_id: 'anakopi_dapm',
          deadline_title: 'Προθεσμία ανακοπής (απόδοση μισθίου)',
          main_title: 'Επίδοση διαταγής απόδοσης μισθίου',
          base_date: data.service_date, base_label: baseLabel,
        });
      } else {
        if (!data.pos_service_no) { toast.error('Επιλέξτε αν είναι πρώτη ή δεύτερη επίδοση'); setSubmitting(false); return; }
        if (data.pos_service_no === 'first') {
          await calculateAndCreate({
            template_id: 'astiki_proti_epidosi_dp', deadline_id: 'anakopi_dp_proti',
            deadline_title: 'Προθεσμία ανακοπής (1η επίδοση)',
            main_title: 'Πρώτη επίδοση διαταγής πληρωμής',
            base_date: data.service_date, base_label: baseLabel,
          });
        } else {
          await calculateAndCreate({
            template_id: 'astiki_defteri_epidosi_dp', deadline_id: 'anakopi_dp_defteri',
            deadline_title: 'Προθεσμία ανακοπής (2η επίδοση)',
            main_title: 'Δεύτερη επίδοση διαταγής πληρωμής',
            base_date: data.service_date, base_label: baseLabel,
          });
        }
      }
      toast.success('Καταχωρήθηκε επιτυχώς');
      onCreated && onCreated();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || err.message || 'Σφάλμα');
    } finally {
      setSubmitting(false);
    }
  };

  // ===== Branching submit: Πράξη αναγκαστικής εκτέλεσης =====
  const submitEnforcementAction = async () => {
    if (!data.enf_remedy) { toast.error('Επιλέξτε ένδικο βοήθημα'); return; }
    if (!data.event_date) { toast.error('Συμπληρώστε την ημερομηνία'); return; }
    setSubmitting(true);
    try {
      const baseLabel = `Ημ/νία: ${fmtDate(data.event_date)}`;
      if (data.enf_remedy === '954') {
        if (!data.enf_954_old) { toast.error('Διευκρινίστε πότε επιδόθηκε η επιταγή προς εκτέλεση'); setSubmitting(false); return; }
        await calculateAndCreate({
          template_id: 'astiki_anakopi_954', deadline_id: 'anakopi_954',
          deadline_title: 'Προθεσμία κατάθεσης ανακοπής (954 ΚΠολΔ)',
          main_title: 'Πράξη αναγκαστικής εκτέλεσης — Ανακοπή 954 ΚΠολΔ',
          base_date: data.event_date, base_label: `Ημ/νία πλειστηριασμού: ${fmtDate(data.event_date)}`,
          options: data.enf_954_old === 'yes' ? { pre_2026_epitagi: true } : {},
        });
      } else {
        // 933 — sub-branches
        if (!data.enf_934_kind) { toast.error('Επιλέξτε περ. 934 α ή β'); setSubmitting(false); return; }
        let template_id, deadline_id, title;
        if (data.enf_934_kind === '934a') {
          if (!data.enf_934a) { toast.error('Επιλέξτε αν αφορά κατάσχεση ή άμεση εκτέλεση'); setSubmitting(false); return; }
          if (data.enf_934a === 'seizure') {
            template_id = 'astiki_anakopi_933_katasxesi'; deadline_id = 'anakopi_933_katasxesi';
            title = 'Ανακοπή 933 (κατάσχεση)';
          } else {
            template_id = 'astiki_anakopi_933_amesi'; deadline_id = 'anakopi_933_amesi';
            title = 'Ανακοπή 933 (άμεση εκτέλεση)';
          }
        } else {
          if (!data.enf_934b) { toast.error('Επιλέξτε υποπερίπτωση 934 β'); setSubmitting(false); return; }
          if (data.enf_934b === 'last_action') {
            template_id = 'astiki_anakopi_933_teleytaia'; deadline_id = 'anakopi_933_teleytaia';
            title = 'Ανακοπή 933 (τελευταία πράξη εκτέλεσης)';
          } else if (data.enf_934b === 'auction_movables') {
            template_id = 'astiki_anakopi_933_plist_kiniton'; deadline_id = 'anakopi_933_plist_kin';
            title = 'Ανακοπή 933 (πλειστηριασμός κινητών)';
          } else {
            template_id = 'astiki_anakopi_933_plist_akiniton'; deadline_id = 'anakopi_933_plist_akin';
            title = 'Ανακοπή 933 (πλειστηριασμός ακινήτων)';
          }
        }
        await calculateAndCreate({
          template_id, deadline_id,
          deadline_title: 'Προθεσμία ανακοπής 933 ΚΠολΔ',
          main_title: title,
          base_date: data.event_date, base_label: baseLabel,
        });
      }
      toast.success('Καταχωρήθηκε επιτυχώς');
      onCreated && onCreated();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || err.message || 'Σφάλμα');
    } finally {
      setSubmitting(false);
    }
  };

  // ===== Simple submit: Ορισμός δικασίμου =====
  const submitHearingDate = async () => {
    if (!data.hearing_date) { toast.error('Συμπληρώστε την ημερομηνία δικασίμου'); return; }
    setSubmitting(true);
    try {
      await createEntry({
        ...sharedEntryFields(), entry_type: 'hearing',
        title: 'Δικάσιμος', date: data.hearing_date, priority: 'high', status: 'ΣΕ ΑΝΑΜΟΝΗ',
        notes: 'Άρθρο 237 ΚΠολΔ — Ορισμός δικασίμου',
      });
      toast.success('Καταχωρήθηκε επιτυχώς');
      onCreated && onCreated();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || err.message || 'Σφάλμα');
    } finally {
      setSubmitting(false);
    }
  };

  // Helper: subtract N working days (skipping weekends) from a date
  const subtractWorkingDays = (dateISO, n) => {
    const d = new Date(dateISO);
    let remaining = n;
    while (remaining > 0) {
      d.setDate(d.getDate() - 1);
      const dow = d.getDay(); // 0=Sun, 6=Sat
      if (dow !== 0 && dow !== 6) remaining -= 1;
    }
    return d.toISOString().split('T')[0];
  };

  // Helper: count working days between two ISO dates (exclusive of start, inclusive of end)
  const workingDaysBetween = (startISO, endISO) => {
    const s = new Date(startISO);
    const e = new Date(endISO);
    if (e <= s) return 0;
    let count = 0;
    const cur = new Date(s.getTime());
    while (cur < e) {
      cur.setDate(cur.getDate() + 1);
      const dow = cur.getDay();
      if (dow !== 0 && dow !== 6) count += 1;
    }
    return count;
  };

  const submitSwornStatements = async () => {
    const isCivil = procedureType === 'civil';
    const count = parseInt(data.sworn_count, 10) || 0;
    if (count <= 0) {
      toast.error('Ο αριθμός ενόρκων βεβαιώσεων πρέπει να είναι τουλάχιστον 1');
      return;
    }
    if (data.sworn_dates.length !== count || data.sworn_dates.some((d) => !d)) {
      toast.error('Συμπληρώστε όλες τις ημερομηνίες λήψης ενόρκων βεβαιώσεων');
      return;
    }

    if (isCivil) {
      // Civil rule: η ημερομηνία επίδοσης πρέπει να είναι ≥ 2 εργάσιμες ημέρες ΠΡΙΝ τη λήψη.
      // Άρα η σημερινή ημερομηνία πρέπει να αφήνει τουλάχιστον 2 εργάσιμες έως κάθε λήψη.
      const today = new Date().toISOString().split('T')[0];
      for (let i = 0; i < data.sworn_dates.length; i++) {
        const sd = data.sworn_dates[i];
        if (workingDaysBetween(today, sd) < 2) {
          toast.error(`Η ημερομηνία λήψης #${i + 1} είναι πολύ κοντινή. Χρειάζονται τουλάχιστον 2 εργάσιμες ημέρες για την επίδοση της γνωστοποίησης.`);
          return;
        }
      }
    } else {
      // Admin rule: hearing_date required, sworn_date ≥ 2 (calendar) ημέρες πριν τη δικάσιμο
      const hearingDate = data.hearing_date;
      if (!hearingDate) {
        toast.error('Συμπληρώστε την ημερομηνία της δικασίμου');
        return;
      }
      const hearing = new Date(hearingDate);
      for (let i = 0; i < data.sworn_dates.length; i++) {
        const sd = new Date(data.sworn_dates[i]);
        const diffDays = Math.floor((hearing.getTime() - sd.getTime()) / (1000 * 60 * 60 * 24));
        if (diffDays < 2) {
          toast.error(`Η ημερομηνία ${i + 1} πρέπει να είναι τουλάχιστον 2 ημέρες πριν τη δικάσιμο`);
          return;
        }
      }
    }

    setSubmitting(true);
    try {
      // For each sworn statement: create the sworn statement entry + the notification deadline entry
      for (let i = 0; i < data.sworn_dates.length; i++) {
        const swornDate = data.sworn_dates[i];
        // Civil: notification = sworn_date - 2 working days
        // Admin: notification = sworn_date - 10 calendar days
        let notificationISO;
        if (isCivil) {
          notificationISO = subtractWorkingDays(swornDate, 2);
        } else {
          const sworn = new Date(swornDate);
          const notification = new Date(sworn.getTime());
          notification.setDate(notification.getDate() - 10);
          notificationISO = notification.toISOString().split('T')[0];
        }

        const swornArticle = isCivil ? 'Άρθρο 421-424 ΚΠολΔ' : 'Άρθρο 185 παρ. 1 ΚΔΔ';
        const notificationNote = isCivil
          ? 'Καταληκτική ημερομηνία επίδοσης γνωστοποίησης (2 εργάσιμες ημέρες πριν τη λήψη)'
          : 'Καταληκτική ημερομηνία γνωστοποίησης (10 ημέρες πριν τη λήψη)';

        await createEntry({
          ...sharedEntryFields(),
          entry_type: 'court_action',
          title: `Λήψη ένορκης βεβαίωσης ${i + 1}/${count}`,
          date: swornDate,
          priority: 'high',
          status: 'ΣΕ ΑΝΑΜΟΝΗ',
          notes: swornArticle + (data.hearing_date ? ` — Δικάσιμος: ${fmtDate(data.hearing_date)}` : ''),
        });
        await createEntry({
          ...sharedEntryFields(),
          entry_type: 'court_action',
          title: `Επίδοση γνωστοποίησης ένορκης βεβαίωσης ${i + 1}/${count}`,
          date: notificationISO,
          priority: 'high',
          status: 'ΣΕ ΑΝΑΜΟΝΗ',
          notes: notificationNote,
        });
      }

      toast.success('Οι εγγραφές καταχωρήθηκαν επιτυχώς');
      onCreated && onCreated();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.detail || err.message || 'Σφάλμα κατά την καταχώρηση';
      toast.error(typeof msg === 'string' ? msg : 'Σφάλμα');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- RENDER ----
  if (!optionValue) return null;

  // Simple radio-pair helper
  const renderRadioPair = (field, leftValue, leftLabel, rightValue, rightLabel) => (
    <div className="flex items-center gap-2 sm:gap-3">
      {[
        { v: leftValue, l: leftLabel },
        { v: rightValue, l: rightLabel },
      ].map(({ v, l }) => (
        <button
          key={v}
          data-testid={`${field}-${v}-btn`}
          onClick={() => setData((d) => ({ ...d, [field]: v }))}
          className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
            data[field] === v ? 'bg-red-700 border border-red-700 text-white shadow-md shadow-red-900/25' : 'text-slate-600 bg-white border border-stone-300 hover:text-slate-900 hover:border-red-700/50'
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  // ===== Ορισμός δικασίμου (237 ΚΠολΔ) =====
  if (optionValue === 'hearing_date') {
    return (
      <div className="space-y-3 bg-white border border-stone-200 rounded-md p-3" data-testid="other-action-hearing-form">
        <div>
          <Label htmlFor="oa-hearing-date">Ημερομηνία δικασίμου *</Label>
          <DatePickerWithInput
            id="oa-hearing-date"
            data-testid="hearing-date-input"
            value={data.hearing_date}
            onChange={(e) => setData((d) => ({ ...d, hearing_date: e.target.value }))}
          />
          <p className="text-xs text-slate-500 mt-1">Άρθρο 237 ΚΠολΔ</p>
        </div>
        <div className="flex gap-2 pt-2">
          <Button data-testid="other-action-submit-btn" onClick={submitHearingDate} disabled={submitting || !data.hearing_date} className="flex-1 bg-red-700 hover:bg-red-800 text-white">
            {submitting ? 'Καταχώρηση...' : 'Καταχώρηση'}
          </Button>
          <Button variant="outline" onClick={onCancel} disabled={submitting}>Ακύρωση</Button>
        </div>
      </div>
    );
  }

  // ===== Sworn statements (admin OR civil) =====
  if (optionValue === 'sworn_statements' || optionValue === 'sworn_statement') {
    const isCivil = procedureType === 'civil';
    return (
      <div className="space-y-3 bg-white border border-stone-200 rounded-md p-3" data-testid="other-action-sworn-form">
        {!isCivil && (
          <div>
            <Label htmlFor="sworn-hearing-date">Ημερομηνία δικασίμου *</Label>
            <DatePickerWithInput
              id="sworn-hearing-date"
              data-testid="sworn-hearing-date-input"
              value={data.hearing_date}
              onChange={(e) => setData((d) => ({ ...d, hearing_date: e.target.value }))}
            />
          </div>
        )}
        <div>
          <Label htmlFor="sworn-count">Πλήθος ενόρκων βεβαιώσεων *</Label>
          <Input
            id="sworn-count"
            data-testid="sworn-count-input"
            type="number"
            min="1"
            value={data.sworn_count}
            onChange={(e) => {
              const n = Math.max(1, parseInt(e.target.value, 10) || 1);
              setData((d) => ({
                ...d,
                sworn_count: n,
                sworn_dates: Array.from({ length: n }, (_, i) => d.sworn_dates[i] || ''),
              }));
            }}
            className="h-11 bg-white border-stone-300"
          />
        </div>
        {data.sworn_dates.map((sd, i) => (
          <div key={i}>
            <Label htmlFor={`sworn-date-${i}`}>Ημερομηνία λήψης #{i + 1} *</Label>
            <DatePickerWithInput
              id={`sworn-date-${i}`}
              data-testid={`sworn-date-${i}-input`}
              value={sd}
              onChange={(e) => {
                const next = [...data.sworn_dates];
                next[i] = e.target.value;
                setData((d) => ({ ...d, sworn_dates: next }));
              }}
            />
            <p className="text-xs text-slate-500 mt-1">
              {isCivil
                ? 'Η επίδοση γνωστοποίησης πρέπει να γίνει τουλάχιστον 2 εργάσιμες ημέρες πριν τη λήψη.'
                : 'Πρέπει να είναι τουλάχιστον 2 ημέρες πριν τη δικάσιμο. Η γνωστοποίηση πρέπει να έχει επιδοθεί 10 ημέρες πριν.'}
            </p>
          </div>
        ))}
        <div className="flex gap-2 pt-2">
          <Button
            data-testid="other-action-submit-btn"
            onClick={submitSwornStatements}
            disabled={submitting}
            className="flex-1 bg-red-700 hover:bg-red-800 text-white"
          >
            {submitting ? 'Καταχώρηση...' : 'Καταχώρηση'}
          </Button>
          <Button variant="outline" onClick={onCancel} disabled={submitting}>
            Ακύρωση
          </Button>
        </div>
      </div>
    );
  }

  // ===== Branching: Επίδοση οριστικής απόφασης (civil) =====
  if (procedureType === 'civil' && optionValue === 'final_decision_service') {
    return (
      <div className="space-y-3 bg-white border border-stone-200 rounded-md p-3" data-testid="other-action-fds-form">
        <div>
          <Label>Η απόφαση εκδόθηκε από: *</Label>
          {renderRadioPair('fds_court_level', 'first', 'Πρωτοβάθμιο δικαστήριο', 'appeal', 'Δευτεροβάθμιο δικαστήριο')}
        </div>
        {data.fds_court_level === 'first' && (
          <div>
            <Label>Αφορά μικροδιαφορά; *</Label>
            {renderRadioPair('fds_microdiff', 'yes', 'Ναι', 'no', 'Όχι')}
            {data.fds_microdiff === 'yes' && (
              <p className="text-xs text-amber-700 mt-2 p-2 bg-amber-50 rounded">
                ⚠️ Δεν προβλέπεται η άσκηση έφεσης για μικροδιαφορές.
              </p>
            )}
            {data.fds_microdiff === 'no' && (
              <div className="mt-3">
                <Label>Η απόφαση εκδόθηκε ερήμην του εντολέα σας; *</Label>
                {renderRadioPair('fds_erimin', 'yes', 'Ναι (έφεση + ανακοπή ερημοδικίας)', 'no', 'Όχι (μόνο έφεση)')}
              </div>
            )}
          </div>
        )}
        {data.fds_court_level === 'appeal' && (
          <div>
            <Label>Είναι τελεσίδικη; *</Label>
            {renderRadioPair('fds_telesidiki', 'yes', 'Ναι (αναίρεση 60 ημ.)', 'no', 'Όχι')}
          </div>
        )}
        <div>
          <Label>Αντίδικος είναι το Ελληνικό Δημόσιο; *</Label>
          {renderRadioPair('is_public', 'yes', 'Ναι', 'no', 'Όχι')}
        </div>
        <div>
          <Label htmlFor="fds-service-date">Ημερομηνία επίδοσης *</Label>
          <DatePickerWithInput id="fds-service-date" data-testid="fds-service-date-input" value={data.service_date} onChange={(e) => setData((d) => ({ ...d, service_date: e.target.value }))} />
        </div>
        <div>
          <Label htmlFor="fds-result">Αποτέλεσμα</Label>
          <Select value={data.result} onValueChange={(v) => setData((d) => ({ ...d, result: v }))}>
            <SelectTrigger id="fds-result" data-testid="fds-result-select" className="h-11 bg-white border-stone-300"><SelectValue placeholder="Επιλέξτε..." /></SelectTrigger>
            <SelectContent>{RESULT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <p className="text-xs text-slate-500">Σε όλες τις περιπτώσεις: αναστολή τον Αύγουστο· με αντίδικο το Ελληνικό Δημόσιο: 1/7–15/9.</p>
        <div className="flex gap-2 pt-2">
          <Button data-testid="other-action-submit-btn" onClick={submitFinalDecisionService} disabled={submitting} className="flex-1 bg-red-700 hover:bg-red-800 text-white">
            {submitting ? 'Καταχώρηση...' : 'Καταχώρηση'}
          </Button>
          <Button variant="outline" onClick={onCancel} disabled={submitting}>Ακύρωση</Button>
        </div>
      </div>
    );
  }

  // ===== Branching: Επίδοση διαταγής πληρωμής/απόδοσης μισθίου =====
  if (procedureType === 'civil' && optionValue === 'payment_order_service') {
    return (
      <div className="space-y-3 bg-white border border-stone-200 rounded-md p-3" data-testid="other-action-pos-form">
        <div>
          <Label>Είδος διαταγής *</Label>
          {renderRadioPair('pos_kind', 'payment', 'Διαταγή πληρωμής', 'lease', 'Διαταγή απόδοσης μισθίου')}
        </div>
        {data.pos_kind === 'payment' && (
          <div>
            <Label>Πρώτη ή δεύτερη επίδοση; *</Label>
            {renderRadioPair('pos_service_no', 'first', '1η επίδοση (15 εργάσιμες)', 'second', '2η επίδοση (10 εργάσιμες)')}
          </div>
        )}
        <div>
          <Label>Αντίδικος είναι το Ελληνικό Δημόσιο; *</Label>
          {renderRadioPair('is_public', 'yes', 'Ναι', 'no', 'Όχι')}
        </div>
        <div>
          <Label htmlFor="pos-service-date">Ημερομηνία επίδοσης *</Label>
          <DatePickerWithInput id="pos-service-date" data-testid="pos-service-date-input" value={data.service_date} onChange={(e) => setData((d) => ({ ...d, service_date: e.target.value }))} />
        </div>
        <p className="text-xs text-slate-500">Αναστολή τον Αύγουστο· με αντίδικο το Ελληνικό Δημόσιο: 1/7–15/9.</p>
        <div className="flex gap-2 pt-2">
          <Button data-testid="other-action-submit-btn" onClick={submitPaymentOrderService} disabled={submitting} className="flex-1 bg-red-700 hover:bg-red-800 text-white">
            {submitting ? 'Καταχώρηση...' : 'Καταχώρηση'}
          </Button>
          <Button variant="outline" onClick={onCancel} disabled={submitting}>Ακύρωση</Button>
        </div>
      </div>
    );
  }

  // ===== Branching: Πράξη αναγκαστικής εκτέλεσης =====
  if (procedureType === 'civil' && optionValue === 'enforcement_action') {
    return (
      <div className="space-y-3 bg-white border border-stone-200 rounded-md p-3" data-testid="other-action-enf-form">
        <div>
          <Label>Ένδικο βοήθημα *</Label>
          {renderRadioPair('enf_remedy', '933', 'Ανακοπή 933 ΚΠολΔ', '954', 'Ανακοπή 954 ΚΠολΔ')}
        </div>
        <div>
          <Label>Αντίδικος είναι το Ελληνικό Δημόσιο; *</Label>
          {renderRadioPair('is_public', 'yes', 'Ναι', 'no', 'Όχι')}
        </div>
        {data.enf_remedy === '933' && (
          <>
            <div>
              <Label>Περίπτωση 934 *</Label>
              {renderRadioPair('enf_934_kind', '934a', '934α (κατάσχεση/άμεση εκτ.)', '934b', '934β (μεταγενέστερες πράξεις)')}
            </div>
            {data.enf_934_kind === '934a' && (
              <div>
                <Label>Είδος *</Label>
                {renderRadioPair('enf_934a', 'seizure', 'Κατάσχεση (45 ημ.)', 'immediate', 'Άμεση εκτέλεση (30 ημ.)')}
              </div>
            )}
            {data.enf_934_kind === '934b' && (
              <div>
                <Label>Είδος τελευταίας πράξης *</Label>
                <Select value={data.enf_934b} onValueChange={(v) => setData((d) => ({ ...d, enf_934b: v }))}>
                  <SelectTrigger data-testid="enf-934b-select" className="h-11 bg-white border-stone-300"><SelectValue placeholder="Επιλέξτε..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="last_action">Τελευταία πράξη εκτέλεσης (30 ημ.)</SelectItem>
                    <SelectItem value="auction_movables">Πλειστηριασμός κινητών (30 ημ.)</SelectItem>
                    <SelectItem value="auction_immovables">Πλειστηριασμός ακινήτων (60 ημ. από μεταγραφή)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label htmlFor="enf-event-date">
                {data.enf_934_kind === '934a' && data.enf_934a === 'seizure' && 'Ημερομηνία κατάσχεσης *'}
                {data.enf_934_kind === '934a' && data.enf_934a === 'immediate' && 'Ημερομηνία επίδοσης επιταγής *'}
                {data.enf_934_kind === '934b' && data.enf_934b === 'last_action' && 'Ημερομηνία τελευταίας πράξης *'}
                {data.enf_934_kind === '934b' && data.enf_934b === 'auction_movables' && 'Ημερομηνία πλειστηριασμού *'}
                {data.enf_934_kind === '934b' && data.enf_934b === 'auction_immovables' && 'Ημερομηνία μεταγραφής κατασχετήριας *'}
                {!data.enf_934_kind && 'Ημερομηνία *'}
              </Label>
              <DatePickerWithInput id="enf-event-date" data-testid="enf-event-date-input" value={data.event_date} onChange={(e) => setData((d) => ({ ...d, event_date: e.target.value }))} />
            </div>
          </>
        )}
        {data.enf_remedy === '954' && (
          <>
            <div>
              <Label>Η επιταγή προς εκτέλεση επιδόθηκε πριν την 1.1.2026; *</Label>
              <div className="mt-2">
                {renderRadioPair('enf_954_old', 'yes', 'Ναι (15 εργάσιμες)', 'no', 'Όχι (30 εργάσιμες)')}
              </div>
            </div>
            <div>
              <Label htmlFor="enf-auction-date">Ημερομηνία πλειστηριασμού *</Label>
              <DatePickerWithInput id="enf-auction-date" data-testid="enf-auction-date-input" value={data.event_date} onChange={(e) => setData((d) => ({ ...d, event_date: e.target.value }))} />
              <p className="text-xs text-slate-500 mt-1">
                {data.enf_954_old === 'yes'
                  ? 'Προθεσμία κατάθεσης έως 15 εργάσιμες ημέρες ΠΡΙΝ τον πλειστηριασμό (προ ν. 5221/2025).'
                  : 'Προθεσμία κατάθεσης έως 30 εργάσιμες ημέρες ΠΡΙΝ τον πλειστηριασμό (ν. 5221/2025).'}
              </p>
            </div>
          </>
        )}
        <p className="text-xs text-slate-500">Αναστολή τον Αύγουστο (εκτός 954).</p>
        <div className="flex gap-2 pt-2">
          <Button data-testid="other-action-submit-btn" onClick={submitEnforcementAction} disabled={submitting} className="flex-1 bg-red-700 hover:bg-red-800 text-white">
            {submitting ? 'Καταχώρηση...' : 'Καταχώρηση'}
          </Button>
          <Button variant="outline" onClick={onCancel} disabled={submitting}>Ακύρωση</Button>
        </div>
      </div>
    );
  }

  // Standard configurable actions
  if (!config) return null;

  return (
    <div className="space-y-3 bg-white border border-stone-200 rounded-md p-3" data-testid="other-action-form">
      {config.show_decision_number && (
        <div>
          <Label htmlFor="other-decision-number">Αριθμός απόφασης *</Label>
          <Input
            id="other-decision-number"
            data-testid="other-decision-number-input"
            value={data.decision_number}
            onChange={(e) => setData((d) => ({ ...d, decision_number: e.target.value }))}
            placeholder="π.χ. 1234/2025"
            className="h-11 bg-white border-stone-300"
          />
        </div>
      )}
      {procedureType === 'civil' && (
        <div>
          <Label>Αντίδικος είναι το Ελληνικό Δημόσιο; *</Label>
          {renderRadioPair('is_public', 'yes', 'Ναι', 'no', 'Όχι')}
        </div>
      )}
      <div>
        <Label htmlFor="other-base-date">{config.base_label}</Label>
        <DatePickerWithInput
          id="other-base-date"
          data-testid="other-base-date-input"
          value={data[config.base_field]}
          onChange={(e) => setData((d) => ({ ...d, [config.base_field]: e.target.value }))}
        />
      </div>
      {config.show_result && (
        <div>
          <Label htmlFor="other-result">Αποτέλεσμα</Label>
          <Select value={data.result} onValueChange={(v) => setData((d) => ({ ...d, result: v }))}>
            <SelectTrigger
              id="other-result"
              data-testid="other-result-select"
              className="h-11 bg-white border-stone-300"
            >
              <SelectValue placeholder="Επιλέξτε αποτέλεσμα..." />
            </SelectTrigger>
            <SelectContent>
              {RESULT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="flex gap-2 pt-2">
        <Button
          data-testid="other-action-submit-btn"
          onClick={submitStandard}
          disabled={submitting || !data[config.base_field] || (config.show_decision_number && !data.decision_number)}
          className="flex-1 bg-red-700 hover:bg-red-800 text-white"
        >
          {submitting ? 'Καταχώρηση...' : 'Καταχώρηση'}
        </Button>
        <Button variant="outline" onClick={onCancel} disabled={submitting}>
          Ακύρωση
        </Button>
      </div>
    </div>
  );
};

// Export the option keys so EntryDetail can know which values trigger this form
// (combines admin + civil + branching + simple flows)
export const OTHER_ACTION_OPTIONS = [
  // Admin (from configKey "administrative:*")
  'first_instance_service', 'first_instance_publication',
  'appeal_service', 'appeal_publication',
  'appeal_cancellation_service', 'appeal_cancellation_publication',
  'final_rejection',
  'sworn_statements',
  'ste_filing',
  'ste_appeal_service',
  // Civil simple (configKey "civil:*")
  'cancellation', 'withdrawal',
  'final_decision_publication', 'payment_order_issue',
  // Civil branching
  'final_decision_service', 'payment_order_service', 'enforcement_action',
  // Civil simple (no template)
  'hearing_date', 'sworn_statement',
];

export default OtherActionForm;
